import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/JobUpload.css';
import '../CSS/DirectorySearch.css';

interface Placement {
  placementId: number;
  placementSource: string;
  placementTitle: string;
  createdDate: string;
}

type SortField = 'placementId' | 'placementSource' | 'placementTitle' | 'createdDate';
type SortDirection = 'asc' | 'desc';

const PlacementDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [filteredPlacements, setFilteredPlacements] = useState<Placement[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPlacements, setSelectedPlacements] = useState<Set<number>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const filterPopupRef = useRef<HTMLDivElement>(null);

  // Fetch placements from API
  useEffect(() => {
    const fetchPlacements = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        // Production API endpoint
        // const response = await fetch('/api/placements', {
        const response = await fetch('/api/placements', {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        const data = await response.json();

        if (data.success && data.data) {
          const placementsData: Placement[] = data.data.map((placement: any) => ({
            placementId: placement.placementId,
            placementSource: placement.placementSource,
            placementTitle: placement.placementTitle,
            createdDate: placement.createdDate || placement.lastUpdated || ''
          }));
          setPlacements(placementsData);
          setFilteredPlacements(placementsData);
        } else {
          setPlacements([]);
          setFilteredPlacements([]);
        }
      } catch (error) {
        console.error('Error fetching placements:', error);
        setPlacements([]);
        setFilteredPlacements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlacements();
  }, []);

  // Filter placements based on search query and date
  useEffect(() => {
    let filtered = [...placements];

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(placement =>
        placement.placementId.toString().includes(query) ||
        placement.placementSource.toLowerCase().includes(query) ||
        placement.placementTitle.toLowerCase().includes(query)
      );
    }

    // Apply date filter (filter by createdDate)
    if (startDate || endDate) {
      filtered = filtered.filter(placement => {
        if (!placement.createdDate) return false;
        
        const createdDate = new Date(placement.createdDate);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate + 'T23:59:59') : null; // Include full day for end date
        
        if (start && end) {
          // Both dates provided: filter between dates
          return createdDate >= start && createdDate <= end;
        } else if (start) {
          // Only start date: filter from start to now
          return createdDate >= start;
        } else if (end) {
          // Only end date: filter all records older than end date
          return createdDate <= end;
        }
        return true;
      });
    }

    setFilteredPlacements(filtered);
    setCurrentPage(1);
  }, [searchQuery, placements, startDate, endDate]);

  // Sort placements
  const sortedPlacements = [...filteredPlacements].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortField === 'createdDate') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedPlacements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPlacements = sortedPlacements.slice(startIndex, startIndex + itemsPerPage);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle checkbox selection
  const handleSelectPlacement = (placementId: number) => {
    const newSelected = new Set(selectedPlacements);
    if (newSelected.has(placementId)) {
      newSelected.delete(placementId);
    } else {
      newSelected.add(placementId);
    }
    setSelectedPlacements(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPlacements.size === paginatedPlacements.length) {
      setSelectedPlacements(new Set());
    } else {
      setSelectedPlacements(new Set(paginatedPlacements.map(placement => placement.placementId)));
    }
  };

  // Handle menu toggle
  const handleMenuToggle = (placementId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === placementId ? null : placementId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Close filter popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
        setShowFilterPopup(false);
      }
    };

    if (showFilterPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPopup]);

  // Handle filter apply
  const handleApplyFilter = () => {
    setShowFilterPopup(false);
    // Filtering is handled by useEffect
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowFilterPopup(false);
  };

  // Handle edit placement
  const handleEditPlacement = (placementId: number) => {
    navigate(`/admin/edit-placement-news?placementId=${placementId}`);
    setOpenMenuId(null);
  };

  // Handle delete placement
  const handleDeletePlacement = async (placementId: number) => {
    if (!window.confirm(`Are you sure you want to delete placement ${placementId}? This action cannot be undone.`)) {
      setOpenMenuId(null);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      // Production API endpoint
      // const response = await fetch(`/api/placements/${placementId}`, {
      const response = await fetch(`/api/placements/${placementId}`, {
        method: 'DELETE',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (response.ok) {
        // Remove placement from state
        setPlacements(prev => prev.filter(placement => placement.placementId !== placementId));
        setSelectedPlacements(prev => {
          const newSet = new Set(prev);
          newSet.delete(placementId);
          return newSet;
        });
        alert('Placement deleted successfully');
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete placement');
      }
    } catch (error: any) {
      console.error('Error deleting placement:', error);
      alert(error.message || 'Error deleting placement. Please try again.');
    } finally {
      setOpenMenuId(null);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (sortedPlacements.length === 0) {
      alert('No placements to export');
      return;
    }

    const headers = ['ID', 'Placement Article Source', 'Placement Article Title', 'Created on'];
    const csvRows = [
      headers.join(','),
      ...sortedPlacements.map(placement => {
        const createdDate = placement.createdDate 
          ? new Date(placement.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        const createdTime = placement.createdDate
          ? new Date(placement.createdDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : '';
        const createdOn = createdDate && createdTime ? `${createdDate} at ${createdTime}` : createdDate || '';
        
        return [
          placement.placementId,
          `"${placement.placementSource.replace(/"/g, '""')}"`,
          `"${placement.placementTitle.replace(/"/g, '""')}"`,
          `"${createdOn}"`
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `placements-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="job-upload-page">
      <div className="job-upload-container" style={{ width: '90vw', maxWidth: 'none' }}>
        {/* Header with Search, Filter, and Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by ID, Placement Article Source or Placement Article Title"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif'
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                pointerEvents: 'none'
              }}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Filter Button */}
          <div style={{ position: 'relative' }} ref={filterPopupRef}>
            <button
              type="button"
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ffffff',
                color: '#000000',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4H14M4 8H12M6 12H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Filter
            </button>
            {showFilterPopup && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.5rem',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d0d0d0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  zIndex: 9999,
                  minWidth: '300px',
                  padding: '1.5rem'
                }}
              >
                <h3 style={{ margin: '0 0 1rem 0', fontFamily: 'Poppins, sans-serif', fontSize: '1rem', fontWeight: '600', letterSpacing: '0.05em' }}>
                  Filter by Created Date
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', fontWeight: '500' }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', fontWeight: '500' }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleApplyFilter}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: '#0E4877',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Apply Filter
                    </button>
                    <button
                      type="button"
                      onClick={handleClearFilter}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/admin/upload-placement-news')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0E4877',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              + New Placement News
            </button>
            <button
              type="button"
              onClick={handleExportCSV}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0E4877',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2V11M8 11L5 8M8 11L11 8M2 13H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export as .CSV
            </button>
            <button
              type="button"
              onClick={() => navigate('/news-and-insights/recent-placements?fromAdmin=true')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0E4877',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2ZM8 12C5.79086 12 4 10.2091 4 8C4 5.79086 5.79086 4 8 4C10.2091 4 12 5.79086 12 8C12 10.2091 10.2091 12 8 12Z" fill="currentColor"/>
                <path d="M8 5C6.34315 5 5 6.34315 5 8C5 9.65685 6.34315 11 8 11C9.65685 11 11 9.65685 11 8C11 6.34315 9.65685 5 8 5Z" fill="currentColor"/>
              </svg>
              View as User
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'left' }}>Loading placements...</div>
        ) : (
          <>
            <div style={{ border: '1px solid #d0d0d0', borderRadius: '8px', overflow: 'visible', backgroundColor: '#ffffff' }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '40px 100px 1.5fr 2fr 200px 60px',
                backgroundColor: '#16416F',
                color: '#ffffff',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedPlacements.size === paginatedPlacements.length && paginatedPlacements.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('placementId')}
                >
                  ID
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'placementId' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'placementId' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('placementSource')}
                >
                  Placement Article Source
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'placementSource' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'placementSource' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('placementTitle')}
                >
                  Placement Article Title
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'placementTitle' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'placementTitle' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('createdDate')}
                >
                  Created on
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div style={{ padding: '1rem' }}></div>
              </div>

              {/* Table Rows */}
              {paginatedPlacements.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'left', color: '#666' }}>No placements found</div>
              ) : (
                paginatedPlacements.map((placement) => (
                  <div
                    key={placement.placementId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 100px 1.5fr 2fr 200px 60px',
                      borderTop: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedPlacements.has(placement.placementId)}
                        onChange={() => handleSelectPlacement(placement.placementId)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {placement.placementId}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {placement.placementSource}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {placement.placementTitle}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {placement.createdDate ? `${formatDate(placement.createdDate)} at ${formatTime(placement.createdDate)}` : ''}
                    </div>
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <button
                        type="button"
                        onClick={(e) => handleMenuToggle(placement.placementId, e)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="5" r="1.5" fill="#666666"/>
                          <circle cx="10" cy="10" r="1.5" fill="#666666"/>
                          <circle cx="10" cy="15" r="1.5" fill="#666666"/>
                        </svg>
                      </button>
                      {openMenuId === placement.placementId && (
                        <div
                          ref={(el) => menuRefs.current[placement.placementId] = el}
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '100%',
                            marginRight: '0.5rem',
                            backgroundColor: '#ffffff',
                            border: '1px solid #d0d0d0',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            zIndex: 9999,
                            minWidth: '150px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditPlacement(placement.placementId)}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid #e0e0e0'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }}
                          >
                            Edit Placement News
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePlacement(placement.placementId)}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              color: '#d32f2f'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }}
                          >
                            Remove this Placement News
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              padding: '1rem 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages || 1}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                    opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlacementDirectory;

